import { registerIntegration } from "@p0security/shared/integrations/registry";
import {
  type AnyFilterSchema,
  Registration,
} from "@p0security/shared/integrations/types";

import { LaunchDarklyAccesses } from "./accesses.ts";
import { LaunchDarklyComponents } from "./components.ts";

// Rule filter values compile to regular expressions, so a rule can say, e.g.,
// that `admin` in `prod-.*` needs approval. `account` matches the account's
// item id.
const LaunchDarklyFilters = {
  account: { keys: ["name"] },
  "base-role": { keys: ["name"] },
} as const satisfies AnyFilterSchema;
export type LaunchDarklyFilters = typeof LaunchDarklyFilters;

// Keyed by component, so a new component doesn't compile until it has one.
// The new item's form labels an item's id with its component's kind, then
// this: "Connector ID", "Account name". An account's name is P0's own, and
// requests use it, as in `acme-prod/writer`.
const IDENTIFIERS: Record<keyof typeof LaunchDarklyComponents, string> = {
  account: "name",
  connector: "ID",
};
// `Registration` hands `identifier` any string, not just a component key.
const identifiers: Partial<Record<string, string>> = IDENTIFIERS;

export const LaunchDarklyRegistration = Registration({
  accesses: LaunchDarklyAccesses,
  category: "resource",
  components: LaunchDarklyComponents,
  // A path here becomes a link in the product, so there's none until the
  // docs pages exist. TODO(INT-743): once its docs pages publish, a later app
  // commit links `launchdarkly/installation` and
  // `launchdarkly/requesting-access`.
  docs: {},
  filters: LaunchDarklyFilters,
  identifier: (component) => identifiers[component] ?? component,
  // Each component is named for what one of its items is.
  itemKind: (component) => component,
  label: "LaunchDarkly",
  logoUrl: "https://launchdarkly.com/favicon.ico",
  maturity: "preview",
  requestType: "requestable",
  // A connector alone grants nothing, so LaunchDarkly counts as installed
  // once an account is.
  useMapping: [{ component: "account", use: "iam_write" }],
});

registerIntegration("launchdarkly", LaunchDarklyRegistration);
